// Ultima linea del menu de usuario: que version esta corriendo esta pestaña.
//
// Es el control interno del version-guard hecho visible — responde "¿estoy
// viendo lo ultimo?" sin abrir devtools. Muestra el sha del commit; el detalle
// completo (buildId, fecha, si el arbol estaba sucio, si hay algo mas nuevo
// publicado) va en el tooltip para no ensuciar el menu.
//
// No es un item accionable: no lleva role="menuitem" ni hover, porque no hace
// nada al clickearlo. Lo unico que hace es disparar un chequeo de version al
// abrirse el menu, para que lo que muestra este fresco.

import { useEffect, useState } from 'react';
import { getVersionGuard, readLocalStamp, type GuardState } from '@version-guard';

export default function VersionChip(): JSX.Element | null {
  const stamp = readLocalStamp();
  const [state, setState] = useState<GuardState | null>(null);

  useEffect(() => {
    const guard = getVersionGuard();
    if (!guard) return;
    // Montarse == abrirse el menu (el dropdown no existe cerrado).
    void guard.checkNow();
    return guard.subscribe(setState);
  }, []);

  // Sin sello no hay nada util que mostrar: un index.html que no paso por el
  // plugin, o el dev server.
  if (stamp.buildId === 'unknown') return null;

  const label = stamp.commitShort ?? stamp.buildId.slice(0, 7);
  const outdated = state?.status === 'update-available';

  const title = [
    `build ${stamp.buildId}`,
    stamp.commitDate ? `commit del ${new Date(stamp.commitDate).toLocaleString('es-AR')}` : null,
    stamp.dirty ? 'construido con cambios sin commitear' : null,
    outdated ? 'Hay una versión más nueva publicada' : 'Estás en la última versión',
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <>
      <div className="border-t border-surface-border dark:border-surface-dark-border" />
      <div
        role="none"
        title={title}
        className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-mono text-ink-subtle select-text"
      >
        <span
          aria-hidden="true"
          className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
            outdated ? 'bg-status-warning' : 'bg-status-success'
          }`}
        />
        <span className="truncate">
          v: {label}
          {stamp.dirty ? '+' : ''}
        </span>
      </div>
    </>
  );
}
